/**
 * First-login tour step list — the client's OWN account, live data, no demo mode.
 *
 * Spec: `knowledge/client-support/specs/2026-08-11-client-support-and-portal-rollout-design.md`
 * §4b. Build sequence: `knowledge/client-support/plans/2026-08-12-build-sequence.md` §5 (3A).
 *
 * SAME ENGINE, SEPARATE LIST — AND THAT IS THE WHOLE POINT
 * -------------------------------------------------------
 * `DemoTour.vue` takes its steps as a prop, so this file is a second `steps`
 * array and nothing else. `demoTourSteps.ts` — Prosser's 14-step sales
 * walkthrough — is not imported, not re-exported, and not modified: the two
 * lists share the renderer and nothing else, which is what the spec means by
 * "the sales/demo tour is untouched".
 *
 * WHY IT IS SEVEN STEPS AND NOT FOURTEEN
 * -------------------------------------
 * The sales tour's job is to show a prospect the whole product. This one's job
 * is to leave a paying client able to do the three things they will actually
 * come back for (§4b):
 *
 *   1. upload a document          → steps 2 and 3
 *   2. respond to a request       → steps 2 and 6
 *   3. check safety scores        → step 4
 *
 * Everything else the portal does is reachable from the sidebar and does not
 * need teaching on day one. Spec caps this at 8; seven leaves room for one
 * addition before that cap has to be argued about.
 *
 * SELECTORS ARE DELIBERATELY SPARSE
 * ---------------------------------
 * Only two steps spotlight an element, and both anchor on wrappers that always
 * render on their route (`[data-tour="action-center"]` on the dashboard's
 * pinned Action Center rail, `[data-tour="driver-roster"]` on the drivers-view
 * root). Everything else is `''` — a parked, undimmed card — because this tour
 * runs on a REAL account whose data we do not control: a client with no
 * accidents, no inspections or an emptied dashboard grid must still get a
 * coherent walkthrough. DemoTour degrades a missing anchor to the same parked
 * card, so a wrong guess is cosmetic rather than fatal, but not guessing is
 * better.
 *
 * NO `seeMore` DRILL-INS, ON PURPOSE
 * ----------------------------------
 * The sales tour's drill-ins open real drawers (an accident record, the §396.9
 * certify dialog). On a live client account those are real rows belonging to
 * real drivers, and a tour that opens someone's file for them is a tour that
 * can leave state behind. The launch tour points; the client clicks.
 */
import type { DemoTourStep } from '../demo/demoTourSteps'

export const LAUNCH_TOUR_STEPS: DemoTourStep[] = [
  {
    route: '/',
    selector: '',
    title: 'Welcome to your portal',
    body: 'This is your compliance record, live — the same information your ClaimShield consultant works from. Two minutes here and you will know how to do the three things that come up most.',
  },
  {
    route: '/',
    selector: '[data-tour="action-center"]',
    title: 'Start here every time',
    body: 'The Action Center is the short list of things that need you — a document we have asked for, a driver waiting on a signature, a renewal coming due. Click "Work through your list" and it walks you item by item. Nothing here is FMCSA jargon; each item says what it wants in plain English.',
  },
  {
    route: '/',
    selector: '[data-tour="action-center"]',
    title: 'Sending us a document',
    body: 'When an item asks for paperwork, the button says "I have it — upload". Pick the file off your phone or desktop and it lands on the right driver\'s file, filed correctly, with no email thread to chase. If you do not have it, the same item can ask the driver for it instead.',
  },
  {
    route: '/safety-scores',
    selector: '',
    title: 'Your safety scores',
    body: 'Your live FMCSA standing — the seven BASIC categories, your ISS score, and which of them is actually costing you. Check this before an insurance renewal or after a bad inspection. The numbers here are the ones FMCSA publishes about you, not our estimate of them.',
  },
  {
    route: '/drivers',
    selector: '[data-tour="driver-roster"]',
    title: 'Every driver, every expiry',
    body: 'CDLs, medical cards, annual reviews and MVRs, tracked with their due dates. Open any driver to see their file and to upload straight onto it — that is the other place documents go, when you have something for a specific driver rather than answering a request.',
  },
  {
    route: '/documents',
    selector: '',
    title: 'Everything we have filed',
    body: 'Every document on your account, searchable, downloadable. When an auditor or an insurer asks for something, this is where you get it — minutes, not an afternoon in a filing cabinet.',
  },
  {
    route: '/messages',
    selector: '',
    title: 'And this reaches your consultant',
    body: 'Messages is an ongoing conversation with the person who handles your account — ask a question here rather than starting an email thread, and it stays on your record so whoever covers can pick it up. You can replay this tour any time from the "?" menu at the top of the page.',
  },
]

/** The same optional tour introduces the setup panel for enrolled clients. */
export function launchTourStepsFor(onboardingVisible: boolean): DemoTourStep[] {
  if (!onboardingVisible) return LAUNCH_TOUR_STEPS
  const steps = LAUNCH_TOUR_STEPS.map((step) => ({ ...step }))
  steps[1] = {
    ...steps[1]!,
    selector: '[data-tour="onboarding-panel"]',
    title: 'Your setup list',
    body: 'Here is what still needs you. Start with this item.',
  }
  steps[2] = {
    ...steps[2]!,
    selector: '[data-tour="onboarding-panel"]',
    body: 'Open a driver card to see the documents still needed. Upload a file you have, or ask the driver to send it.',
  }
  return steps
}
